import { useState } from "react";
import { ArrowDown, ArrowUp, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { Badge, Card, act, cleanLabel, useLoad } from "./shared";

function JobRow({ j, i, n, onSave, onMove }) {
  const [label, setLabel] = useState(j.label);
  return (
    <tr className={j.active ? "" : "bg-slate-50/70"} data-testid={`job-row-${j.id}`}>
      <td className="px-3 py-2 text-xs text-slate-400 tabular-nums">{i + 1}</td>
      <td className="px-3 py-2">
        <Input className="h-8" value={cleanLabel(label)} onChange={(e) => setLabel(e.target.value)} onBlur={() => cleanLabel(label) !== cleanLabel(j.label) && onSave(j, { label })} data-testid={`job-label-${j.id}`} />
        {j.former_labels?.length > 0 && <div className="mt-1 text-[11px] text-slate-400">Formerly: {j.former_labels.map(cleanLabel).join(", ")}</div>}
      </td>
      <td className="px-3 py-2">
        <select className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" value={j.kind || "job"} onChange={(e) => onSave(j, { kind: e.target.value })} data-testid={`job-kind-${j.id}`}>
          <option value="job">Jobsite</option><option value="status">Work status (e.g. Vacation)</option>
        </select>
      </td>
      <td className="px-3 py-2 text-xs text-slate-500 tabular-nums">{j.uses}</td>
      <td className="px-3 py-2"><div className="flex items-center gap-2"><Switch checked={j.active} onCheckedChange={(v) => onSave(j, { active: v })} data-testid={`job-active-${j.id}`} />{j.active ? <Badge tone="green">On form</Badge> : <Badge>Hidden</Badge>}</div></td>
      <td className="px-3 py-2 whitespace-nowrap">
        <button type="button" disabled={i === 0} onClick={() => onMove(i, -1)} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" data-testid={`job-up-${j.id}`}><ArrowUp size={14} /></button>
        <button type="button" disabled={i === n - 1} onClick={() => onMove(i, 1)} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" data-testid={`job-down-${j.id}`}><ArrowDown size={14} /></button>
      </td>
    </tr>
  );
}

export default function JobsTab() {
  const { data, reload, setData } = useLoad("/timesheet/jobs");
  const [name, setName] = useState("");
  const items = data?.items || [];
  const save = async (j, body) => { if (await act(() => api.put(`/timesheet/jobs/${j.id}`, body), "Job updated")) reload(); };
  const add = async () => {
    if (!name.trim()) return;
    if (await act(() => api.post("/timesheet/jobs", { label: name.trim() }), "Job added to the form")) { setName(""); reload(); }
  };
  const move = async (i, d) => {
    const next = [...items];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setData({ items: next });
    await act(() => api.post("/timesheet/jobs/reorder", { ids: next.map((x) => x.id) }));
  };
  return (
    <Card title="Jobs / Properties" testId="raw-jobs"
      subtitle="Active jobs appear as choices on every jobsite question. Hiding a job removes it from new submissions only — history and reports keep it.">
      <div className="mb-4 flex gap-2 max-w-md">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New job or property name" data-testid="job-new-input" />
        <Button onClick={add} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="job-add-btn"><Plus size={14} className="mr-1" />Add</Button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{["#", "Name", "Type", "Times used", "Available", "Order"].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((j, i) => <JobRow key={`${j.id}-${j.label}`} j={j} i={i} n={items.length} onSave={save} onMove={move} />)}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
